import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const s = {
  wrap:      { padding: '28px 32px', maxWidth: 750 },
  h1:        { fontSize: '1.4rem', color: 'var(--admin-text)', fontFamily: 'Inter,sans-serif', fontWeight: 600, marginBottom: 6 },
  sub:       { fontSize: '.78rem', color: 'var(--admin-text-dim)', marginBottom: 28 },
  section:   { background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', borderRadius: 8, padding: '20px 24px', marginBottom: 16 },
  secTitle:  { fontSize: '.68rem', fontWeight: 700, color: 'var(--admin-text-dim)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 16 },
  row:       { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 16 },
  label:     { fontSize: '.82rem', color: 'var(--admin-text-muted)', fontWeight: 500, minWidth: 180, flexShrink: 0 },
  sublabel:  { fontSize: '.68rem', color: 'var(--admin-text-dim)', marginTop: 1 },
  input:     { flex: 1, background: 'var(--admin-bg-input)', border: '1px solid var(--admin-border)', borderRadius: 6, padding: '7px 12px', color: 'var(--admin-text)', fontSize: '.82rem', outline: 'none', fontFamily: 'inherit' },
  select:    { flex: 1, background: 'var(--admin-bg-input)', border: '1px solid var(--admin-border)', borderRadius: 6, padding: '7px 12px', color: 'var(--admin-text)', fontSize: '.82rem', outline: 'none', fontFamily: 'inherit', cursor: 'pointer' },
  toggle:    { display: 'flex', alignItems: 'center', gap: 10 },
  colorWrap: { display: 'flex', alignItems: 'center', gap: 8, flex: 1 },
  colorPick: { width: 34, height: 34, border: '1px solid var(--admin-border)', borderRadius: 6, cursor: 'pointer', background: 'none', padding: 2, flexShrink: 0 },
  colorText: { flex: 1, background: 'var(--admin-bg-input)', border: '1px solid var(--admin-border)', borderRadius: 6, padding: '7px 10px', color: 'var(--admin-text)', fontSize: '.82rem', outline: 'none', fontFamily: 'monospace' },
  btnRow:    { display: 'flex', gap: 10, alignItems: 'center', marginTop: 8 },
  btnSave:   { background: 'var(--admin-accent)', border: 'none', color: 'var(--admin-bg-input)', borderRadius: 6, padding: '9px 22px', fontSize: '.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  btnReset:  { background: 'none', border: '1px solid var(--admin-border)', color: 'var(--admin-text-dim)', borderRadius: 6, padding: '9px 16px', fontSize: '.82rem', cursor: 'pointer', fontFamily: 'inherit' },
  ok:        { fontSize: '.78rem', color: 'var(--admin-success)', marginLeft: 8 },
  err:       { fontSize: '.78rem', color: 'var(--admin-danger)', marginLeft: 8 },
};

export default function Settings() {
  const [form, setForm]         = useState({});
  const [original, setOriginal] = useState({});
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [status, setStatus]     = useState('');

  const [totpRequired, setTotpRequired]   = useState(false);
  const [totpSaving,   setTotpSaving]     = useState(false);
  const [totpStatus,   setTotpStatus]     = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [layoutRes, totpRes] = await Promise.all([
        api.getLayout('__global__'),
        api.totpGetSettings().catch(() => ({ totp_required: false })),
      ]);
      const cfg = layoutRes?.layout?.layout_json || {};
      setForm(cfg);
      setOriginal(cfg);
      setTotpRequired(!!totpRes.totp_required);
    } catch {
      setStatus('error:Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }

  async function handleTotpSave() {
    setTotpSaving(true);
    setTotpStatus('');
    try {
      await api.totpSaveSettings(totpRequired);
      setTotpStatus('ok');
    } catch (e) {
      setTotpStatus('error:' + (e.message || 'Fehler'));
    } finally {
      setTotpSaving(false);
    }
  }

  function set(key, val) {
    setForm(f => ({ ...f, [key]: val }));
    setStatus('');
  }

  async function handleSave() {
    setSaving(true);
    setStatus('');
    try {
      await api.saveLayout('__global__', 'Globales Layout', form, true);
      setOriginal(form);
      setStatus('ok');
    } catch (e) {
      setStatus('error:' + (e.message || 'Fehler'));
    } finally {
      setSaving(false);
    }
  }

  const hasChanges = JSON.stringify(form) !== JSON.stringify(original);

  if (loading) return <div style={s.wrap}><p style={{color:'var(--admin-text-dim)',fontSize:'.78rem'}}>Lade...</p></div>;

  return (
    <div style={s.wrap}>
      <h1 style={s.h1}>Systemeinstellungen</h1>
      <p style={s.sub}>Globale Konfiguration — wird auf allen Seiten angewendet.</p>

      {/* ── Allgemein ── */}
      <div style={s.section}>
        <p style={s.secTitle}>Allgemein</p>
        {[
          { key: 'siteName',   label: 'Site-Name',    placeholder: 'Strixly' },
          { key: 'footerText', label: 'Footer-Text',  placeholder: '© 2026 Strixly' },
        ].map(({ key, label, placeholder }) => (
          <div key={key} style={s.row}>
            <span style={s.label}>{label}</span>
            <input style={s.input} value={form[key] || ''} placeholder={placeholder}
              onChange={e => set(key, e.target.value)} />
          </div>
        ))}

        <div style={s.row}>
          <div>
            <div style={s.label}>Registrierung</div>
          </div>
          <select style={s.select} value={form.registration || 'open'} onChange={e => set('registration', e.target.value)}>
            <option value="open">Offen (jeder kann sich registrieren)</option>
            <option value="invite">Nur Einladung</option>
            <option value="closed">Geschlossen</option>
          </select>
        </div>

        <div style={s.row}>
          <div>
            <div style={s.label}>Wartungsmodus</div>
            <div style={s.sublabel}>Zeigt allen Nicht-Admins eine Wartungsseite</div>
          </div>
          <select style={s.select} value={form.maintenance ? 'on' : 'off'} onChange={e => set('maintenance', e.target.value === 'on')}>
            <option value="off">Aus</option>
            <option value="on">Ein</option>
          </select>
        </div>

        <div style={s.row}>
          <div>
            <div style={s.label}>Max. Upload-Größe</div>
            <div style={s.sublabel}>Maximale Dateigröße für Bilder (MB)</div>
          </div>
          <input style={{...s.input, maxWidth: 100}} type="number" min="1" max="50"
            value={form.maxUploadMb || 10} onChange={e => set('maxUploadMb', Number(e.target.value))} />
        </div>
      </div>

      {/* ── Kontakt & Support ── */}
      <div style={s.section}>
        <p style={s.secTitle}>Kontakt & Support</p>
        {[
          { key: 'contactMail',  label: 'Kontakt-Mail',        sub: 'Öffentlich auf der Website angezeigt', placeholder: 'hallo@strixly.at' },
          { key: 'supportMail',  label: 'Support-Mail',        sub: 'Intern — für Nutzer-Anfragen', placeholder: 'support@strixly.at' },
          { key: 'operatorName', label: 'Betreiber (Impressum)', sub: 'Name/Firma für Impressumspflicht', placeholder: 'Max Mustermann' },
          { key: 'operatorAddr', label: 'Adresse (Impressum)',  sub: 'Adresse für Impressumspflicht', placeholder: 'Musterstraße 1, 1010 Wien' },
        ].map(({ key, label, sub, placeholder }) => (
          <div key={key} style={s.row}>
            <div>
              <div style={s.label}>{label}</div>
              <div style={s.sublabel}>{sub}</div>
            </div>
            <input style={s.input} value={form[key] || ''} placeholder={placeholder}
              onChange={e => set(key, e.target.value)} />
          </div>
        ))}
      </div>

      {/* ── E-Mail / Script ── */}
      <div style={s.section}>
        <p style={s.secTitle}>E-Mail & Automatisierung</p>

        <div style={s.row}>
          <div>
            <div style={s.label}>Script-Empfänger</div>
            <div style={s.sublabel}>Wohin automatische E-Mails gesendet werden</div>
          </div>
          <input style={s.input} type="email" value={form.scriptMailTo || ''} placeholder="admin@strixly.at"
            onChange={e => set('scriptMailTo', e.target.value)} />
        </div>

        <div style={s.row}>
          <div>
            <div style={s.label}>Script-Absender</div>
            <div style={s.sublabel}>From-Adresse für automatische Mails</div>
          </div>
          <input style={s.input} type="email" value={form.scriptMailFrom || ''} placeholder="noreply@strixly.at"
            onChange={e => set('scriptMailFrom', e.target.value)} />
        </div>

        <div style={s.row}>
          <div>
            <div style={s.label}>Script-Zeitplan</div>
            <div style={s.sublabel}>Wann läuft das automatische Script</div>
          </div>
          <select style={s.select} value={form.scriptSchedule || 'daily'} onChange={e => set('scriptSchedule', e.target.value)}>
            <option value="disabled">Deaktiviert</option>
            <option value="hourly">Stündlich</option>
            <option value="daily">Täglich</option>
            <option value="weekly">Wöchentlich</option>
          </select>
        </div>

        <div style={s.row}>
          <div>
            <div style={s.label}>Script-Uhrzeit</div>
            <div style={s.sublabel}>Tageszeit für tägliche/wöchentliche Ausführung</div>
          </div>
          <input style={{...s.input, maxWidth: 120}} type="time" value={form.scriptTime || '08:00'}
            onChange={e => set('scriptTime', e.target.value)} />
        </div>
      </div>

      {/* ── Sicherheit ── */}
      <div style={s.section}>
        <p style={s.secTitle}>Sicherheit</p>
        <div style={s.row}>
          <div>
            <div style={s.label}>2FA-Pflicht</div>
            <div style={s.sublabel}>Alle Benutzer müssen Zwei-Faktor-Authentifizierung einrichten</div>
          </div>
          <div style={s.toggle}>
            <select style={{...s.select, maxWidth: 160}}
              value={totpRequired ? 'on' : 'off'}
              onChange={e => { setTotpRequired(e.target.value === 'on'); setTotpStatus(''); }}>
              <option value="off">Freiwillig</option>
              <option value="on">Pflicht für alle</option>
            </select>
            <button style={s.btnSave} onClick={handleTotpSave} disabled={totpSaving}>
              {totpSaving ? '…' : 'Speichern'}
            </button>
            {totpStatus === 'ok' && <span style={s.ok}>✓</span>}
            {totpStatus.startsWith('error:') && <span style={s.err}>✗ {totpStatus.slice(6)}</span>}
          </div>
        </div>
      </div>

      <div style={s.btnRow}>
        <button style={{...s.btnSave, opacity: saving || !hasChanges ? 0.5 : 1}}
          onClick={handleSave} disabled={saving || !hasChanges}>
          {saving ? 'Speichern...' : 'Speichern'}
        </button>
        {hasChanges && <button style={s.btnReset} onClick={() => { setForm(original); setStatus(''); }}>Zurücksetzen</button>}
        {status === 'ok' && <span style={s.ok}>✓ Gespeichert</span>}
        {status.startsWith('error:') && <span style={s.err}>✗ {status.slice(6)}</span>}
      </div>
    </div>
  );
}
